import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  AlertTriangle,
  Clock,
  Package,
  Calendar,
  Bell,
  BellOff,
  RefreshCw,
  Search,
  Filter,
  Settings,
  Download,
  Trash2,
  Edit,
  Save,
  X,
  CheckCircle,
  XCircle,
  Mail,
  MessageSquare,
} from "lucide-react";
import toast from "react-hot-toast";

const API = `${window.location.origin}/api`;

export default function ExpiryAlertsPage() {
  const [alerts, setAlerts] = useState({
    expired: [],
    expiring_soon: [],
    out_of_stock: [],
    low_stock: [],
    summary: {}
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("priority");
  const [settings, setSettings] = useState({
    alertDays: 30,
    emailNotifications: false,
    smsNotifications: false,
    autoDelete: false
  });
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    fetchAlerts();
    loadSettings();
  }, []);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products/inventory-alerts`);
      setAlerts(response.data);
    } catch (error) {
      toast.error("حدث خطأ في جلب التنبيهات");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = () => {
    const saved = localStorage.getItem('expiry_alert_settings');
    if (saved) {
      setSettings(JSON.parse(saved));
    }
  };

  const saveSettings = () => {
    localStorage.setItem('expiry_alert_settings', JSON.stringify(settings));
    toast.success("تم حفظ الإعدادات");
    setShowSettings(false);
  };

  const getDaysUntilExpiry = (expiryDate) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getAllAlerts = () => {
    let all = [
      ...alerts.expired.map(a => ({ ...a, category: 'expired' })),
      ...alerts.expiring_soon.map(a => ({ ...a, category: 'expiring' })),
      ...alerts.out_of_stock.map(a => ({ ...a, category: 'out_of_stock' })),
      ...alerts.low_stock.map(a => ({ ...a, category: 'low_stock' }))
    ];

    // فلترة حسب البحث
    if (searchQuery) {
      all = all.filter(a => a.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }

    // فلترة حسب النوع
    if (filter !== "all") {
      all = all.filter(a => a.category === filter);
    }

    // الترتيب
    all.sort((a, b) => {
      if (sortBy === "priority") {
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      }
      if (sortBy === "date" && a.expiry_date && b.expiry_date) {
        return new Date(a.expiry_date) - new Date(b.expiry_date);
      }
      if (sortBy === "name") {
        return a.name.localeCompare(b.name);
      }
      return 0;
    });

    return all;
  };

  const exportToCSV = () => {
    const allAlerts = getAllAlerts();
    const headers = ["المنتج", "النوع", "الأولوية", "تاريخ الانتهاء", "الكمية"];
    const rows = allAlerts.map(a => [
      a.name,
      a.alert_type,
      a.priority,
      a.expiry_date || "-",
      a.stock !== undefined ? a.stock : "-"
    ]);
    
    const csvContent = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `alerts_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success("تم تصدير التنبيهات بنجاح");
  };

  const getAlertBadge = (category) => {
    switch (category) {
      case 'expired':
        return <Badge className="bg-red-500 text-white">منتهي الصلاحية</Badge>;
      case 'expiring':
        return <Badge className="bg-amber-500 text-white">قريب الانتهاء</Badge>;
      case 'out_of_stock':
        return <Badge className="bg-red-500 text-white">نفد المخزون</Badge>;
      case 'low_stock':
        return <Badge className="bg-purple-500 text-white">مخزون منخفض</Badge>;
      default:
        return null;
    }
  };

  const totalAlerts = alerts.summary?.total_alerts || 0;
  const filteredAlerts = getAllAlerts();

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="expiry-alerts-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            totalAlerts > 0 
              ? 'bg-gradient-to-br from-red-500 to-orange-600 shadow-lg shadow-red-500/30' 
              : 'bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/30'
          }`}>
            <AlertTriangle className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">
              تنبيهات انتهاء الصلاحية
            </h1>
            <p className="text-slate-400 text-sm">
              {totalAlerts > 0 ? `${totalAlerts} تنبيه نشط` : 'لا توجد تنبيهات'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={fetchAlerts}
            disabled={loading}
            className="bg-slate-800 hover:bg-slate-700"
          >
            <RefreshCw className={`w-4 h-4 me-2 ${loading ? 'animate-spin' : ''}`} />
            تحديث
          </Button>
          <Button
            onClick={exportToCSV}
            className="bg-emerald-500 hover:bg-emerald-600"
          >
            <Download className="w-4 h-4 me-2" />
            تصدير
          </Button>
          <Button
            onClick={() => setShowSettings(!showSettings)}
            variant="outline"
            className="border-slate-600"
          >
            <Settings className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* الإعدادات */}
      {showSettings && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Settings className="w-5 h-5" />
              إعدادات التنبيهات
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">
                  أيام التنبيه قبل الانتهاء
                </label>
                <Input
                  type="number"
                  value={settings.alertDays}
                  onChange={(e) => setSettings({...settings, alertDays: parseInt(e.target.value)})}
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.emailNotifications}
                    onChange={(e) => setSettings({...settings, emailNotifications: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-300">إشعارات البريد</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.smsNotifications}
                    onChange={(e) => setSettings({...settings, smsNotifications: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <MessageSquare className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-300">إشعارات SMS</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowSettings(false)} className="border-slate-600">
                <X className="w-4 h-4 me-2" />
                إلغاء
              </Button>
              <Button onClick={saveSettings} className="bg-emerald-500 hover:bg-emerald-600">
                <Save className="w-4 h-4 me-2" />
                حفظ
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* إحصائيات سريعة */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-red-500/10 to-red-600/10 border-red-500/30">
          <CardContent className="p-4 text-center">
            <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-red-400">{alerts.summary?.expired_count || 0}</p>
            <p className="text-sm text-slate-400">منتهي الصلاحية</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 border-amber-500/30">
          <CardContent className="p-4 text-center">
            <Clock className="w-8 h-8 text-amber-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-amber-400">{alerts.summary?.expiring_soon_count || 0}</p>
            <p className="text-sm text-slate-400">قريب الانتهاء</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-500/10 to-pink-600/10 border-red-500/30">
          <CardContent className="p-4 text-center">
            <XCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-red-400">{alerts.summary?.out_of_stock_count || 0}</p>
            <p className="text-sm text-slate-400">نفد المخزون</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 border-purple-500/30">
          <CardContent className="p-4 text-center">
            <Package className="w-8 h-8 text-purple-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-purple-400">{alerts.summary?.low_stock_count || 0}</p>
            <p className="text-sm text-slate-400">مخزون منخفض</p>
          </CardContent>
        </Card>
      </div>

      {/* البحث والفلاتر */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="بحث عن منتج..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-slate-700/50 border-slate-600 text-white pr-10"
              />
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="bg-slate-700/50 border border-slate-600 text-white rounded-lg px-4 py-2"
            >
              <option value="all">جميع التنبيهات</option>
              <option value="expired">منتهي الصلاحية</option>
              <option value="expiring">قريب الانتهاء</option>
              <option value="out_of_stock">نفد المخزون</option>
              <option value="low_stock">مخزون منخفض</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-slate-700/50 border border-slate-600 text-white rounded-lg px-4 py-2"
            >
              <option value="priority">ترتيب حسب الأولوية</option>
              <option value="date">ترتيب حسب التاريخ</option>
              <option value="name">ترتيب أبجدي</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* قائمة التنبيهات */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-emerald-500 mx-auto mb-4" />
              <p className="text-slate-400">جاري التحميل...</p>
            </div>
          ) : filteredAlerts.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h3 className="text-xl text-white mb-2">لا توجد تنبيهات!</h3>
              <p className="text-slate-400">جميع المنتجات في حالة ممتازة</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredAlerts.map((alert, index) => {
                const days = alert.expiry_date ? getDaysUntilExpiry(alert.expiry_date) : null;
                return (
                  <div
                    key={`${alert.id}-${index}`}
                    className={`p-4 rounded-lg border transition-all hover:scale-[1.01] ${
                      alert.category === 'expired' || alert.category === 'out_of_stock'
                        ? 'bg-red-500/10 border-red-500/30'
                        : alert.category === 'expiring'
                          ? 'bg-amber-500/10 border-amber-500/30'
                          : 'bg-purple-500/10 border-purple-500/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-xl ${
                          alert.category === 'expired' || alert.category === 'out_of_stock'
                            ? 'bg-red-500/20'
                            : alert.category === 'expiring'
                              ? 'bg-amber-500/20'
                              : 'bg-purple-500/20'
                        }`}>
                          {alert.category === 'expired' || alert.category === 'expiring' ? (
                            <Clock className="w-6 h-6" />
                          ) : (
                            <Package className="w-6 h-6" />
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium text-lg">{alert.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {getAlertBadge(alert.category)}
                            {alert.expiry_date && (
                              <span className="text-sm text-slate-400 flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {new Date(alert.expiry_date).toLocaleDateString('ar-IQ')}
                              </span>
                            )}
                            {alert.stock !== undefined && (
                              <span className="text-sm text-slate-400">
                                الكمية: {alert.stock}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-left">
                        {days !== null && (
                          <div className={`text-xl font-bold ${
                            days < 0 ? 'text-red-400' : days <= 7 ? 'text-orange-400' : 'text-amber-400'
                          }`}>
                            {days < 0 ? (
                              <span>منتهي منذ {Math.abs(days)} يوم</span>
                            ) : days === 0 ? (
                              <span className="animate-pulse">ينتهي اليوم!</span>
                            ) : (
                              <span>متبقي {days} يوم</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
